import { useState, useRef, useLayoutEffect } from "react";
import { Room } from "./Room.jsx";
import { prefersReduced } from "../utils.js";

export default function Reveal() {
  const [pos, setPos] = useState(56);
  const touched = useRef(false);

  useLayoutEffect(() => {
    if (prefersReduced()) return undefined;
    setPos(100);
    let raf = 0, t0 = null, stopped = false;
    const from = 100, to = 56, delay = 450, dur = 1700;
    const step = (t) => {
      if (stopped || touched.current) return;
      if (t0 === null) t0 = t;
      const e = t - t0 - delay;
      if (e < 0) { raf = requestAnimationFrame(step); return; }
      const k = Math.min(1, e / dur);
      setPos(from + (to - from) * (1 - Math.pow(1 - k, 3)));
      if (k < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => { stopped = true; cancelAnimationFrame(raf); };
  }, []);

  const take = () => { touched.current = true; };

  return (
    <figure className="wrap reveal">
      <div className="stage">
        <svg className="layer draft" viewBox="0 0 1200 560" preserveAspectRatio="xMidYMid slice" role="img"
             aria-label="Dimensioned elevation drawing of a kitchen wall with cabinets, a range, and a window over the sink">
          <defs>
            <pattern id="p-grid" width="24" height="24" patternUnits="userSpaceOnUse">
              <path className="gl" d="M24 0H0V24" />
            </pattern>
          </defs>
          <rect className="paper" x="0" y="0" width="1200" height="560" />
          <rect className="grid" x="0" y="0" width="1200" height="560" />
          <Room built={false} />
        </svg>
        <svg className="layer built" viewBox="0 0 1200 560" preserveAspectRatio="xMidYMid slice" aria-hidden="true"
             style={{ clipPath: `inset(0 0 0 ${pos}%)` }}>
          <defs>
            <linearGradient id="g-steel" x1="0" x2="1" y1="0" y2="0">
              <stop offset="0" style={{ stopColor: "#AEB6BC" }} />
              <stop offset="0.45" style={{ stopColor: "#E4E7E9" }} />
              <stop offset="1" style={{ stopColor: "#A9B1B7" }} />
            </linearGradient>
            <linearGradient id="g-glass" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0" style={{ stopColor: "var(--k-glass1)" }} />
              <stop offset="1" style={{ stopColor: "var(--k-glass2)" }} />
            </linearGradient>
            <linearGradient id="g-glow" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0" style={{ stopColor: "#FFE6AE", stopOpacity: 0.6 }} />
              <stop offset="1" style={{ stopColor: "#FFE6AE", stopOpacity: 0 }} />
            </linearGradient>
            <linearGradient id="g-floor" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0" style={{ stopColor: "var(--k-floor)" }} />
              <stop offset="1" style={{ stopColor: "var(--k-floor2)" }} />
            </linearGradient>
            <pattern id="p-tile" width="28.8" height="28.8" patternUnits="userSpaceOnUse">
              <rect className="tb" x="0" y="0" width="28.8" height="28.8" />
              <path className="tg" d="M0 0H28.8M0 14.4H28.8M0 28.8H28.8M0 0V14.4M14.4 14.4V28.8" />
            </pattern>
          </defs>
          <Room built={true} />
        </svg>
        <span className="tag tag-l">Drawing</span>
        <span className="tag tag-r">Built</span>
        <input id="reveal" className="slider" type="range" min="0" max="100" step="any" value={pos}
               aria-label="Compare the drawing with the finished kitchen"
               aria-valuetext={`${Math.round(100 - pos)} percent built`}
               onPointerDown={take} onKeyDown={take} onTouchStart={take}
               onChange={(e) => { take(); setPos(parseFloat(e.target.value)); }} />
        <div className="handle" style={{ left: `${pos}%` }}>
          <span className="grip">
            <svg width="22" height="22" viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M8 6l-5 5 5 5" />
              <path d="M14 6l5 5-5 5" />
            </svg>
          </span>
        </div>
      </div>
      <figcaption className="cap">North wall of a 1962 kitchen. Drag the handle to compare the drawing with the finished room.</figcaption>
    </figure>
  );
}
